import { type Papel } from '@/nucleo/auth/papeis';
import { type DreDeLotacao } from '@/nucleo/auth/tipos';

/**
 * Funções puras que a navegação usa para se apresentar.
 *
 * Ficam fora dos arquivos de componente de propósito: arquivo que exporta
 * componente E função perde a atualização rápida do Vite, e o estado da aplicação
 * — inclusive a sessão, que vive em memória — se perde a cada salvamento.
 */

/**
 * Item ativo do menu.
 *
 * A raiz da área só fica ativa no caminho exato — senão ela ficaria acesa em toda
 * subrota, e duas coisas apareceriam ativas ao mesmo tempo. Os demais itens
 * aceitam subrota (`/operacao/veiculos/123` acende "Veículos"), porque detalhe e
 * formulário pertencem à mesma jornada.
 */
export function estaAtivo(
  caminhoAtual: string,
  caminhoDoItem: string,
  raizDaArea: string,
): boolean {
  if (caminhoDoItem === raizDaArea) {
    return caminhoAtual === raizDaArea;
  }
  return caminhoAtual === caminhoDoItem || caminhoAtual.startsWith(`${caminhoDoItem}/`);
}

/**
 * O caminho do menu que responde pelo endereço atual — um só, para que a barra
 * lateral acenda um item por vez.
 *
 * `estaAtivo` sozinho não basta quando um item é prefixo de outro: o
 * "Centro de Parametrização" (`/admin/parametros`) acenderia em toda tela de
 * parâmetro, inclusive nas que têm item próprio mais abaixo, e o detalhe de uma
 * tela de submenu (`/operacao/sorteios/:id`) não acenderia o filho, que só
 * comparava caminho exato. A regra aqui é a do roteamento: vence o caminho
 * exato; sem ele, o prefixo mais longo — é o item mais específico da jornada em
 * que a pessoa está.
 *
 * A raiz da área continua só no caminho exato (ver `estaAtivo`). Um mesmo
 * caminho declarado em dois lugares do menu devolve o mesmo valor, e os dois
 * acendem, como já acontecia com a comparação exata.
 */
export function caminhoAtivoDoMenu(
  caminhoAtual: string,
  caminhosDoMenu: Iterable<string>,
  raizDaArea: string,
): string | undefined {
  let melhor: string | undefined;
  for (const caminho of caminhosDoMenu) {
    if (caminho === caminhoAtual) return caminho;
    if (!estaAtivo(caminhoAtual, caminho, raizDaArea)) continue;
    if (!melhor || caminho.length > melhor.length) melhor = caminho;
  }
  return melhor;
}

/**
 * Papel de DRE, sem nenhum papel de SME ou admin junto — a mesma distinção que
 * o protótipo chama de "DRE puro" (`SmeLayout.tsx`, `isDrePuro`), usada pra
 * trocar o rótulo de um item de menu que SME e DRE dividem (`tituloParaDre`).
 */
export function ehSoDre(papeis: readonly Papel[] | undefined): boolean {
  if (!papeis || papeis.length === 0) {
    return false;
  }
  const temDre = papeis.includes('GESTOR_DRE') || papeis.includes('OPERADOR_DRE');
  const temSmeOuAdmin =
    papeis.includes('GESTOR_SME') || papeis.includes('OPERADOR_SME') || papeis.includes('ADMIN');
  return temDre && !temSmeOuAdmin;
}

/**
 * Rótulo de escopo mostrado no topo da sidebar de SME — igual ao protótipo
 * (`SmeLayout.tsx`, `escopoLabel`): "Todas as DREs" pra quem vê tudo (SME ou
 * admin), o nome da própria DRE pra quem é DRE puro. O nome vem da lotação que
 * o `GET /eu` devolve (`dre`); sem ela, cai no mesmo fallback genérico que o
 * protótipo usa quando ainda não carregou.
 */
export function escopoDeSme(
  papeis: readonly Papel[] | undefined,
  dre?: DreDeLotacao | null,
): string | undefined {
  if (!papeis || papeis.length === 0) {
    return undefined;
  }
  if (ehSoDre(papeis)) {
    return nomeDaDre(dre);
  }
  const vePainelInteiro =
    papeis.includes('ADMIN') ||
    papeis.includes('GESTOR_ADMIN') ||
    papeis.includes('GESTOR_SME') ||
    papeis.includes('OPERADOR_SME');
  return vePainelInteiro ? 'Todas as DREs' : undefined;
}

/**
 * Nome da diretoria como o rótulo de escopo o mostra: "DRE Butantã".
 *
 * A sincronização do EOL grava o nome já com o prefixo ("DRE Butantã"), mas a
 * tabela também tem linhas de outras origens; o prefixo só entra quando falta,
 * para nunca sair "DRE DRE Butantã". Sem lotação, ou com nome vazio, fica o
 * "DRE" genérico de antes.
 */
export function nomeDaDre(dre: DreDeLotacao | null | undefined): string {
  const nome = dre?.nome?.trim();
  if (!nome) {
    return 'DRE';
  }
  return /^DRE(\s|$)/i.test(nome) ? nome : `DRE ${nome}`;
}

/** Iniciais para o avatar. Duas letras bastam, e nome vazio não vira caixa vazia. */
export function iniciais(nome: string | null): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);

  if (partes.length === 0) {
    return '?';
  }
  if (partes.length === 1) {
    return partes[0]!.slice(0, 2).toUpperCase();
  }
  return `${partes[0]![0]}${partes[partes.length - 1]![0]}`.toUpperCase();
}

/** Badge do menu: acima de 99 vira "99+", pra não estourar o espaço da barra lateral. */
export function formatarContagemBadge(contagem: number): string {
  return contagem > 99 ? '99+' : String(contagem);
}

/** Rótulo do breadcrumb: acima do limite, corta e acrescenta reticências. */
export function truncarRotulo(rotulo: string, limite = 24): string {
  return rotulo.length > limite ? `${rotulo.slice(0, limite).trimEnd()}…` : rotulo;
}
